import { FC, forwardRef, Fragment, ReactElement, Ref, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Dialog, Divider, styled, Tooltip, AppBar, Toolbar, IconButton, Typography, Slide, Grid, Paper, FormControlLabel, Checkbox as MuiCheckbox, List, ListItemButton, ListItemText, ListItem, ListItemIcon, alpha, Snackbar } from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/pro-duotone-svg-icons";
import { BasicAlert, Checkbox, InputOnlyField, Markdown, Text } from "@ngt/forms";
import ConfigurationFormEditPanel, { ConfigurationBox } from './ConfigurationFormEditPanel';
import { IConfigurationEvent, ConfigurationType, ISourceDataConfigurationItem } from '@/utilities/forms/ConfigurationFormProcessor';
import { useScopedField, useFormActions } from '@ngt/forms-core';
import { IEventMetadata, IFormFieldMetadata, IFormFieldValueMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata, ISourceDataConfigurationEvent, ISourceDataConfigurationFormFieldValue } from '@/api/dtos';
import { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import { faTimes, faRight } from "@fortawesome/pro-solid-svg-icons";
import { TransitionProps } from "@mui/material/transitions";
import ConfigurationFormEditInfo from './ConfigurationFormEditInfo';
import { set } from "lodash-es";
import { ISourceDataConfigurationFormField } from '../../../../api/dtos';
import { IConfigurationFormEditUpdateItem, ISelectionOption, IUpdateMapping } from "./update";
import { ConfigurationUpdateFieldMapping } from "./update/field";
import { ConfigurationUpdateValueMapping } from "./update/value";
import { ConfigurationUpdateSectionMapping } from "./update/section";
import { ConfigurationUpdateVersionMapping } from "./update/version";
import { ConfigurationUpdateFormMapping } from "./update/form";
import { ConfigurationUpdateEventMapping } from "./update/event";
import { IConfigurationFormEditUpdateProps, InfoBox, Selections, UpdateDialogTransition } from "./update/Common";
import { faCheckCircle } from "@fortawesome/pro-light-svg-icons";


  
const ConfigurationFormEditUpdate: FC<IConfigurationFormEditUpdateProps> = ({
    target,
    source
}) => {
    const [open, setOpen] = useState(false);
    const [snack, setSnack] = useState(false);

    const showButton = (!target.isTrial || !!source.isTrial) && (!source.linked);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const onUpdate = () => {
        setOpen(false);
        setSnack(true);
    };

    const handleSnackClose = () => {
        setSnack(false);
    };

    return (
        <>
            {
                showButton && (
                    <Tooltip
                        title={
                            target.isTrial ?
                                `Update trial configuration for ${target.formVersionMetadata?.name ?? 'Unknown Version'}.` :
                            target.isGlobal ?
                                `Update global configuration for ${target.formVersionMetadata?.name ?? 'Unknown Version'}.` :
                                `Update ${target.eventMetadata?.name ?? 'Unknown Event'} configuration for ${target.formVersionMetadata?.name ?? 'Unknown Version'}.`
                        }
                    >
                        <Button variant="contained" size="small" sx={{ml: 2}} onClick={handleOpen}>
                            Update
                        </Button>
                    </Tooltip>
                )
            }
            <Dialog
                fullScreen
                open={open && showButton}
                onClose={handleClose}
                TransitionComponent={UpdateDialogTransition}
            >
                <AppBar position='sticky'>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            { 
                                target.isTrial ?
                                `Update Trial Configuration` :
                            target.isGlobal ?
                                `Update Global Configuration` :
                                `Update ${target.eventMetadata?.name ?? 'Unknown Event'} Configuration`
                            }
                        </Typography>
                    </Toolbar>
                </AppBar>
                <Grid 
                    container
                    spacing={2}
                >
                    <Grid 
                        item
                        xs={12}
                        md={5}
                    >
                        <InfoBox>
                            <ConfigurationFormEditInfo
                                {...source}
                                dividers
                            />
                        </InfoBox>
                    </Grid>
                    <Grid 
                        item
                        xs={12}
                        md={2}
                        sx={{
                            textAlign: 'center',
                            alignSelf: 'center',
                            color: 'secondary.main'
                        }}
                    >
                        <FontAwesomeIcon icon={faRight} size="10x" />
                    </Grid>
                    <Grid 
                        item
                        xs={12}
                        md={5}
                    >
                        <InfoBox>
                            <ConfigurationFormEditInfo
                                {...target}
                                dividers
                            />
                        </InfoBox>
                    </Grid>
                </Grid>
                <Selections source={source} target={target} buttonText="Update" onUpdate={onUpdate} />
            </Dialog>
            <Snackbar
                open={snack}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right'}}
                onClose={handleSnackClose}
            >
                <div>
                    <BasicAlert 
                        severity="success"
                        icon={faCheckCircle}
                        title="Updated Successfully"
                        description={
                            target.isTrial ?
                                `Updated trial configuration for ${target.formVersionMetadata?.name ?? 'Unknown Version'}.` :
                            target.isGlobal ?
                                `Updated global configuration for ${target.formVersionMetadata?.name ?? 'Unknown Version'}.` :
                                `Updated ${target.eventMetadata?.name ?? 'Unknown Event'} configuration for ${target.formVersionMetadata?.name ?? 'Unknown Version'}.`
                        }
                    />
                </div>
            </Snackbar>
        </>
    )
}

export default ConfigurationFormEditUpdate;